// lib/services/compute/yuv_manager.ts

import { YuvFormat, YuvPlaneInfo, YuvUploadDescriptor } from "./yuvdefs";

/**
 * Stateless utility for parsing VideoFrame plane layouts and reslicing
 * YUV byte buffers for GPU upload.
 *
 * In the previous engine YuvManager also owned GPU textures and uploaded
 * planes itself. That responsibility now lives in ColorCameraSlot (texture
 * ownership) and CaptureService (writeTexture calls). YuvManager is just
 * the byte-layout helper.
 *
 * Lifetime: one instance per CaptureService is fine. It keeps a single
 * reusable staging buffer for VideoFrame.copyTo, growing it on demand.
 */
export default class YuvManager {
  private staging_buffer: Uint8Array;

  constructor() {
    this.staging_buffer = new Uint8Array(1);
  }

  /**
   * Returns a staging buffer at least `size` bytes long. The same buffer
   * is reused frame-to-frame until a size increase forces reallocation.
   *
   * Callers pass this buffer to VideoFrame.copyTo and then to
   * build_descriptor + slice_plane.
   */
  ensure_staging_buffer(size: number): Uint8Array {
    if (!this.staging_buffer || this.staging_buffer.byteLength !== size) {
      this.staging_buffer = new Uint8Array(size);
    }
    return this.staging_buffer;
  }

  /**
   * Returns true if a buffer of the requested size required reallocation.
   * Useful for callers that need to know when a downstream resource (e.g.
   * GPU textures sized from frame dimensions) must be rebuilt.
   */
  resize_staging_buffer(size: number): boolean {
    if (!this.staging_buffer || this.staging_buffer.byteLength !== size) {
      this.staging_buffer = new Uint8Array(size);
      return true;
    }
    return false;
  }

  /**
   * Copy a span of bytes into a fresh GPU-writable Uint8Array. WebGPU's
   * writeTexture requires the source byte view to fully back its
   * underlying ArrayBuffer (no offset, full length), so we copy out of
   * the staging buffer rather than aliasing it.
   */
  slice_plane(
    data: Uint8Array,
    offset: number,
    length: number
  ): Uint8Array<ArrayBuffer> {
    const out = new Uint8Array(length);
    out.set(data.subarray(offset, offset + length));
    return out;
  }

  /**
   * Parse a PlaneLayout[] (returned by VideoFrame.copyTo) into a
   * YuvUploadDescriptor. The descriptor's plane width/height fields are
   * derived from layout stride + the span between consecutive plane
   * offsets.
   *
   * Validates plane count per format and throws if the layout is
   * inconsistent (zero strides, byteLength not divisible by stride, etc.).
   */
  build_descriptor(
    format: YuvFormat,
    layout: PlaneLayout[] | ReadonlyArray<PlaneLayout>,
    data_byte_length: number,
    coded_width: number,
    coded_height: number,
    display_width: number,
    display_height: number
  ): YuvUploadDescriptor {
    if (!layout.length) {
      throw new Error("YuvManager.build_descriptor: layout is empty");
    }

    const plane_byte_length = (plane_index: number): number => {
      const start = layout[plane_index]?.offset;
      const end =
        plane_index + 1 < layout.length
          ? layout[plane_index + 1].offset
          : data_byte_length;

      if (start == null || end == null || end < start) {
        throw new Error(
          `YuvManager.build_descriptor: invalid plane span at index ${plane_index} (start=${start}, end=${end}, total=${data_byte_length})`
        );
      }
      return end - start;
    };

    const plane_height_from_span = (
      stride: number,
      byte_length: number,
      label: string
    ): number => {
      if (stride <= 0) {
        throw new Error(`${label}: invalid stride ${stride}`);
      }
      if (byte_length % stride !== 0) {
        throw new Error(
          `${label}: byteLength ${byte_length} is not divisible by stride ${stride}`
        );
      }
      return byte_length / stride;
    };

    if (format === "I420") {
      if (layout.length !== 3) {
        throw new Error(
          `YuvManager.build_descriptor: I420 expected 3 planes, got ${layout.length}`
        );
      }

      const y_byte_length = plane_byte_length(0);
      const u_byte_length = plane_byte_length(1);
      const v_byte_length = plane_byte_length(2);

      const y_stride = layout[0].stride;
      const u_stride = layout[1].stride;
      const v_stride = layout[2].stride;

      const y_height = plane_height_from_span(y_stride, y_byte_length, "I420 Y");
      const u_height = plane_height_from_span(u_stride, u_byte_length, "I420 U");
      const v_height = plane_height_from_span(v_stride, v_byte_length, "I420 V");

      const y_plane: YuvPlaneInfo = {
        offset: layout[0].offset,
        stride: y_stride,
        width: y_stride,
        height: y_height,
        byteLength: y_byte_length,
      };
      const u_plane: YuvPlaneInfo = {
        offset: layout[1].offset,
        stride: u_stride,
        width: u_stride,
        height: u_height,
        byteLength: u_byte_length,
      };
      const v_plane: YuvPlaneInfo = {
        offset: layout[2].offset,
        stride: v_stride,
        width: v_stride,
        height: v_height,
        byteLength: v_byte_length,
      };

      return {
        format: "I420",
        codedWidth: coded_width,
        codedHeight: coded_height,
        displayWidth: display_width,
        displayHeight: display_height,
        planes: [y_plane, u_plane, v_plane],
      };
    }

    if (format === "NV12") {
      if (layout.length !== 2) {
        throw new Error(
          `YuvManager.build_descriptor: NV12 expected 2 planes, got ${layout.length}`
        );
      }

      const y_byte_length = plane_byte_length(0);
      const uv_byte_length = plane_byte_length(1);

      const y_stride = layout[0].stride;
      const uv_stride = layout[1].stride;

      const y_height = plane_height_from_span(y_stride, y_byte_length, "NV12 Y");
      const uv_height = plane_height_from_span(uv_stride, uv_byte_length, "NV12 UV");

      const y_plane: YuvPlaneInfo = {
        offset: layout[0].offset,
        stride: y_stride,
        width: y_stride,
        height: y_height,
        byteLength: y_byte_length,
      };
      // NV12 interleaves Cb and Cr in one rg8 plane. The texture is
      // `stride / 2` rg8 texels wide.
      const uv_plane: YuvPlaneInfo = {
        offset: layout[1].offset,
        stride: uv_stride,
        width: Math.floor(uv_stride / 2),
        height: uv_height,
        byteLength: uv_byte_length,
      };

      return {
        format: "NV12",
        codedWidth: coded_width,
        codedHeight: coded_height,
        displayWidth: display_width,
        displayHeight: display_height,
        planes: [y_plane, uv_plane],
      };
    }

    throw new Error(`YuvManager.build_descriptor: unsupported format ${format}`);
  }
}